import { Injectable } from '@angular/core';
import { SessionStore } from './session.store';
import { catchError, finalize, tap } from 'rxjs/operators';
import type { LoginResponse, VerifyResponse, Profile } from './session.model';
import { HttpErrorResponse, HttpHeaders, HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { BusinessContextService } from '../business/business-context.service';
import { BusinessStore } from '../business/business.store';
import type { UserSignUpRequestDto } from '../../../../../../api/src/user/dtos/user-sign-up-request.dto';
import type { PublicBusiness } from '../../../../../../api/src/business/business.entity';
import { arrayRemove, arrayUpsert } from '@datorama/akita';
import type { BusinessUser } from '../../../../../../api/src/business/business-user.entity';
import type { Hierarchy } from '../../../../../../api/src/_core/third-party/wpp-open/models';
import type { AuthFlow, AuthMethod } from '../../../../../../api/src/user/user.entity';
import { defer, throwError } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class SessionService {
	public basePath = environment.apiUrl;
	public defaultHeaders = new HttpHeaders({
		Accept: 'application/json'
	});

	constructor(
		private sessionStore: SessionStore,
		protected httpClient: HttpClient,
		private readonly businessStore: BusinessStore,
		private readonly businessContext: BusinessContextService
	) {}

	public wppOpenLogin(
		token: string,
		siteId: string,
		workspaceId?: string,
		scopeId?: string,
		projectRemoteId?: string,
		projectRemoteName?: string,
		hierarchy?: Hierarchy,
		tenantId?: string
	) {
		const headers = this.defaultHeaders;
		this.sessionStore.setLoading(true);

		return this.httpClient
			.post<LoginResponse>(
				`${this.basePath}/user/auth/wpp-open/sign-in`,
				{
					token,
					siteId: siteId,
					workspaceId,
					scopeId,
					projectRemoteId,
					projectRemoteName,
					hierarchy,
					tenantId
				},
				{
					headers
				}
			)
			.pipe(
				tap(resp => {
					this.sessionStore.login({
						redirect: resp.redirect,
						token: resp.token,
						profile: resp.profile,
						businessUser: resp.businessUser
					});

					this.businessStore.set(resp?.profile?.businesses || []);
					this.businessContext.resolveActiveBusiness();

					this.sessionStore.setLoading(false);
				}),
				catchError(err => {
					this.sessionStore.setLoading(false);
					throw err;
				})
			);
	}

	public requestCode(loginCode: string, authMethod?: AuthMethod, authFlow?: AuthFlow) {
		const headers = this.defaultHeaders;
		this.sessionStore.setLoading(true);

		const loginData = this.getLoginData(loginCode);

		return (
			this.httpClient
				.post<VerifyResponse>(
					`${environment.apiUrl}/user/auth/request-sign-in`,
					{ ...loginData, siteId: environment.organizationId, authMethod, authFlow },
					{ headers }
				)
				// .post<LoginResponse>(
				// 	`${this.basePath}/user/auth/basic/generate-sign-in-code`,
				// 	{ email, siteId: environment.organizationId },
				// 	{ headers }
				// )
				.pipe(
					tap(() => this.sessionStore.setLoading(false)),
					catchError(err => {
						this.sessionStore.setLoading(false);
						throw err;
					})
				)
		);
	}

	/**
	 * Local-dev only: fetches the first TEST_USERS email from the API so the UI can
	 * drive a hands-free "Dev auto-login". The endpoint is guarded by LOCALHOST on the
	 * API and returns 403/404 in any deployed environment, so callers should tolerate
	 * failure and simply not offer auto-login when it errors.
	 */
	public getDevAutoLoginUser() {
		const headers = this.defaultHeaders;

		return this.httpClient.get<{ email: string }>(`${this.basePath}/user/auth/dev/auto-login-user`, { headers });
	}

	public signUp(request: Partial<UserSignUpRequestDto>) {
		const headers = this.defaultHeaders;
		this.sessionStore.setLoading(true);
		request.siteId = environment.organizationId;

		return this.httpClient.post<LoginResponse>(`${this.basePath}/user/signup`, request, { headers }).pipe(
			tap(resp => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.sessionStore.setLoading(false);
				throw err;
			})
		);
	}

	public activateCode(loginCode: string, singlePass: string, authMethod?: AuthMethod) {
		const loginData = this.getLoginData(loginCode);

		// http call to verify code
		const params = {
			singlePass,
			authMethod,
			...loginData,
			siteId: environment.organizationId
		};
		const headers = this.defaultHeaders;
		this.sessionStore.setLoading(true);

		return this.httpClient.post<LoginResponse>(`${this.basePath}/user/auth/basic/code-sign-in`, params, { headers }).pipe(
			tap(resp => {
				this.sessionStore.login({
					redirect: resp.redirect,
					token: resp.token,
					profile: resp.profile,
					businessUser: resp.businessUser
				});

				this.businessStore.set(resp?.profile?.businesses || []);
				this.businessContext.resolveActiveBusiness();

				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.sessionStore.setLoading(false);
				throw err;
			})
		);
	}

	public getUserStatus(token: string) {
		return defer(() => {
			const previousHydrationStatus = this.sessionStore.getValue().profileHydrationStatus;
			this.sessionStore.beginProfileHydration();
			this.sessionStore.setLoading(true);

			return this.httpClient.get<LoginResponse>(`${this.basePath}/user/me`).pipe(
				tap((resp: LoginResponse) => {
					this.sessionStore.login({
						token: resp.token,
						profile: resp.profile,
						businessUser: resp.businessUser
					});

					this.businessStore.set(resp?.profile?.businesses || []);
					this.businessContext.resolveActiveBusiness();
				}),
				catchError((err: HttpErrorResponse) => {
					if (err.status === 401 || err.status === 403) {
						this.logout();
					} else {
						this.sessionStore.resolveProfileHydrationAfterFailure(previousHydrationStatus);
					}

					return throwError(() => err);
				}),
				finalize(() => {
					// Also resolve a request that was cancelled before success/error so
					// authorization guards cannot remain pending indefinitely.
					this.sessionStore.resolveProfileHydrationAfterFailure(previousHydrationStatus);
					this.sessionStore.setLoading(false);
				})
			);
		});
	}

	public updateProfile(profile: Partial<Profile['settings']>) {
		//this.sessionStore.updateProfile(profile);
		const headers = this.defaultHeaders;
		return this.httpClient.put(`${this.basePath}/user/settings`, profile, { headers }).pipe(
			tap((resp: any) => {
				this.sessionStore.updateProfile({
					settings: resp.settings
				});
			})
		);
	}

	public upsertBusinessToProfile(business: PublicBusiness) {
		this.sessionStore.update(state => ({
			...state,
			profile: {
				...state.profile,
				businesses: arrayUpsert(state.profile.businesses || [], business.id, business)
			}
		}));
	}

	public upsertBusinessUsers(businessUsers: BusinessUser[]) {
		if (!businessUsers?.length) {
			return;
		}

		this.sessionStore.update(state => {
			let currentUsers = state.businessUser || [];

			businessUsers.forEach(businessUser => (currentUsers = arrayUpsert(currentUsers, businessUser.id, businessUser)));

			return {
				...state,
				businessUser: currentUsers
			};
		});
	}

	public removeBusinessFromProfile(businessId: PublicBusiness['id']) {
		this.sessionStore.update(state => ({
			...state,
			profile: {
				...state.profile,
				businesses: arrayRemove(state.profile.businesses || [], businessId)
			}
		}));
	}

	public inviteUsers(inviteArray: any) {
		const headers = this.defaultHeaders;

		return this.httpClient.post(`${this.basePath}/user/invite/${inviteArray.businessId}`, inviteArray, { headers }).pipe(
			tap((resp: any) => {
				// this.sessionStore.updateProfile({
				// 	settings: resp.settings
				// });
			})
		);
	}

	public acceptTerms() {
		return this.httpClient.put(`${this.basePath}/user/accept-terms`, {}).pipe(
			tap((resp: any) => {
				this.sessionStore.update(state => ({
					...state,
					profile: {
						...state.profile,
						...resp
					}
				}));
			})
		);
	}

	public logout() {
		this.businessContext.clearPersistedContext();
		this.sessionStore.logout();
		this.businessStore.set([]);
	}

	public getLoginData(loginCode: string) {
		if (loginCode?.includes('@')) {
			return { email: loginCode };
		}

		//phone number
		if (!loginCode?.startsWith('+')) {
			loginCode = '+1' + loginCode;
		}

		return { phone: loginCode };
	}
}
